import { useMemo, useCallback, useState } from 'react';
import {
  Table,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TableCell,
  TableBody,
  Box,
  CircularProgress,
  Button,
  Tooltip
} from '@mui/material';
import { useNavigate, useParams } from 'react-router';
import { useSearchParams } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import moment from 'moment';
import { Header, tableContainerStyle } from 'components/tables/common';
import { formatCurrency } from 'utils';
import {
  bulkMarkManualSoldOut,
  getManualSoldOutEligibility,
  ManualSoldOutEligibilityResult
} from 'services/scratcherService';
import {
  getCompleteCellClass,
  getDataCellClass
} from 'components/tables/common/simpleTableClasses';
import noVarianceIcon from 'assets/images/mappedicon.svg';
import { AuditDetailItem } from 'models';
import type { AuditNoteEntry } from 'models/audit/AuditDetailModel';
import '../../style/AuditDetailTable.scss';

const getEligibilityKey = (gameId: string | number | null | undefined, packId: string | number | null | undefined) => {
  const gameDigits = String(gameId ?? '').replace(/\D/g, '');
  const packDigits = String(packId ?? '').replace(/\D/g, '');
  if (!gameDigits || !packDigits) return '';
  return `${Number(gameDigits)}-${Number(packDigits)}`;
};

const formatBackOnSaleTimestamp = (value: string | null | undefined) => {
  if (!value) return '';
  return moment.parseZone(value).format('MM/DD/YYYY [@] h:mm A');
};

const getBackOnSaleDestination = (subgroup: string | null | undefined, hasReturnedToCounterEvent = false) => {
  if (subgroup === 'Vending') return 'Vending';
  if (subgroup === 'Counter') return 'Counter';
  if (hasReturnedToCounterEvent) return 'Counter';
  return '';
};

const getBackstockLocationLabel = (subgroup: string | null | undefined) => {
  if (subgroup === 'Backstock') return 'Backstock';
  if (subgroup === 'Backstock Locker') return 'Locker';
  return 'Backstock';
};

export default function AuditDetailTable({
  data,
  isLoading = false,
  isBothConnected = false,
  isBothNotConnected = false,
  isLottoConnectedOnly = false,
  isPOSConnectedOnly = false,
  tableView = 'default',
  totalPacksCount = 0,
  auditNotes = []
}: {
  data: AuditDetailItem[];
  isLoading?: boolean;
  isBothConnected?: boolean;
  isBothNotConnected?: boolean;
  isLottoConnectedOnly?: boolean;
  isPOSConnectedOnly?: boolean;
  tableView?: 'default' | 'moved_to_backstock';
  totalPacksCount?: number;
  auditNotes?: AuditNoteEntry[];
}) {
  const navigate = useNavigate();
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const queryClient = useQueryClient();
  const [markingSoldOutKey, setMarkingSoldOutKey] = useState<string | null>(null);
  const isMovedToBackstockView = tableView === 'moved_to_backstock';
  const auditDate = searchParams.get('auditDate');

  // Check if posType exists and is not 'n/a'
  const hasPosType = isBothConnected || isPOSConnectedOnly;

  // Total columns: 5 (Game, Pack, Inventory: Range, Sold Qty, Sold $) + 4 (POS: Sold Qty, Sales $, Reconciliation: Variance Qty, Variance) if hasPosType
  const TOTAL_COLS = isMovedToBackstockView ? 6 : (hasPosType ? 9 : 5);
  const borderColumns = isMovedToBackstockView ? [0, 1, 2, 3, 4, 5] : (hasPosType ? [0, 1, 4, 6] : [0, 1, 4]);

  const getRowBgColor = (item: AuditDetailItem) => {
    // Don't show background colors when there's no posType
    if (!hasPosType || isMovedToBackstockView) return undefined;

    const qv = item.quantityVariance;
    const rv = item.revenueVariance;

    if (qv < 0 || rv < 0) return '#FF000014';
    if (qv > 0 || rv > 0) return '#2c6ca014';

    return undefined;
  };

  const getMovedRange = (item: any) => {
    const explicitRange = item?.moved_to_backstock_range ?? item?.movedToBackstockRange ?? item?.backstockRange ?? item?.range;
    if (explicitRange) return String(explicitRange);

    const ticketNumberAtMove = Number(item?.ticket_number_at_move ?? item?.ticketNumberAtMove ?? 0);
    const ticketsRemaining = Number(item?.tickets_remaining ?? item?.ticketsRemaining ?? 0);
    if (ticketNumberAtMove > 0) {
      const start = Number(ticketNumberAtMove % 1000);
      const endRaw = ticketsRemaining === 0 ? ticketNumberAtMove : (ticketNumberAtMove + ticketsRemaining);
      const end = Number(endRaw % 1000);
      return `${start.toString().padStart(3, '0')}-${end.toString().padStart(3, '0')}`;
    }

    const start = Number(item?.startingTicket ?? item?.starting_ticket_number ?? 0);
    const end = Number(item?.endingTicket ?? item?.ending_ticket_number ?? 0);
    if (start > 0 || end > 0) {
      return `${(start % 1000).toString().padStart(3, '0')}-${(end % 1000).toString().padStart(3, '0')}`;
    }

    return '-';
  };

  const getMovedQty = (item: any) => {
    return Number(
      item?.moved_to_backstock_count ??
      item?.movedToBackstockCount ??
      item?.backstockMovedCount ??
      item?.tickets_remaining ??
      item?.ticketsRemaining ??
      item?.qty_moved_to_backstock ??
      item?.qtyMovedToBackstock ??
      0
    );
  };

  const getMovedValue = (item: any) => {
    const explicitValue = Number(
      item?.moved_to_backstock_value ??
      item?.movedToBackstockValue ??
      item?.backstockMovedValue ??
      NaN
    );
    if (!Number.isNaN(explicitValue)) return explicitValue;
    return getMovedQty(item) * Number(item?.costPerTicket ?? item?.game_cost_per_ticket ?? 0);
  };

  const movedPackEligibilityRequest = useMemo(() => {
    if (!isMovedToBackstockView) return [];

    const seenKeys = new Set<string>();
    const packs: Array<{ game_id: string; pack_id: string }> = [];
    data.forEach((item: any) => {
      const gameDigits = String(item?.gameId ?? '').replace(/\D/g, '');
      const packDigits = String(item?.packId ?? '').replace(/\D/g, '');
      if (!gameDigits || !packDigits) return;
      const key = `${Number(gameDigits)}-${Number(packDigits)}`;
      if (seenKeys.has(key)) return;
      seenKeys.add(key);
      packs.push({ game_id: gameDigits, pack_id: packDigits });
    });
    return packs;
  }, [data, isMovedToBackstockView]);

  const {
    data: manualSoldOutEligibility = [],
    isFetching: isManualSoldOutEligibilityLoading,
  } = useQuery({
    queryKey: ['manual-sold-out-eligibility', id, movedPackEligibilityRequest],
    queryFn: async () => {
      if (!id) return [];
      const response = await getManualSoldOutEligibility(id, movedPackEligibilityRequest);
      if (!response.success) {
        throw new Error(response.error || 'Failed to load pack sold-out eligibility');
      }
      return response.data?.results ?? [];
    },
    enabled: Boolean(id && isMovedToBackstockView && movedPackEligibilityRequest.length > 0),
    retry: false,
  });

  const manualSoldOutEligibilityByKey = useMemo(() => {
    const map = new Map<string, ManualSoldOutEligibilityResult>();
    manualSoldOutEligibility.forEach((item) => {
      map.set(getEligibilityKey(item.game_id, item.pack_id), item);
    });
    return map;
  }, [manualSoldOutEligibility]);

  const formatTicketSegment = (ticketNumber: number | null | undefined) => {
    if (ticketNumber == null || ticketNumber === -1) return 'END';
    return (Number(ticketNumber) % 1000).toString().padStart(3, '0');
  };

  const getTicketDisplay = (
    displayValue: string | undefined,
    ticketNumber: number | null | undefined
  ) => {
    return displayValue ?? formatTicketSegment(ticketNumber);
  };

  const formatAuditNoteTimestamp = (entry: AuditNoteEntry): string | null => {
    const rawValue = entry.created_at_local || entry.created_at;
    if (!rawValue) return null;

    const parsed = moment.parseZone(rawValue);
    return parsed.isValid() ? parsed.format('MM/DD/YYYY h:mm A') : rawValue;
  };

  const visibleAuditNotes = useMemo(
    () => auditNotes.filter((entry) => entry.text?.trim()),
    [auditNotes]
  );

  const groupedDefaultRows = useMemo(() => {
    if (isMovedToBackstockView) return [];

    const groups = new Map<string, AuditDetailItem[]>();
    data.forEach((item) => {
      const key = item.gameId || item.id;
      if (!groups.has(key)) groups.set(key, []);
      groups.get(key)!.push(item);
    });

    const rows: Array<{
      item: AuditDetailItem;
      isFirstInGroup: boolean;
      groupSize: number;
      globalRowIdx: number;
      rowColor: string | undefined;
      groupStripeClass: string;
    }> = [];

    let globalRowIdx = 0;
    let groupIdx = 0;
    groups.forEach((items) => {
      const groupSize = items.length;
      const rowColor = getRowBgColor(items[0]);
      const groupStripeClass = groupIdx % 2 === 0 ? 'audit-table__row--even' : 'audit-table__row--odd';
      items.forEach((item, idx) => {
        rows.push({
          item,
          isFirstInGroup: idx === 0,
          groupSize,
          globalRowIdx,
          rowColor,
          groupStripeClass
        });
        globalRowIdx += 1;
      });
      groupIdx += 1;
    });

    return rows;
  }, [data, isMovedToBackstockView]);

  // Expensive calculation, keep memoized
  const totals = useMemo(() => {
    if (isMovedToBackstockView) {
      return data.reduce(
        (acc, item: any) => {
          const movedQty = getMovedQty(item);
          const movedValue = getMovedValue(item);
          return {
            qty_sold: acc.qty_sold + movedQty,
            sold_amount: acc.sold_amount + movedValue,
            scratcher_count: 0,
            scratcher_sales: 0,
            quantity_variance: 0,
            revenue_variance: 0,
          };
        },
        {
          qty_sold: 0,
          sold_amount: 0,
          scratcher_count: 0,
          scratcher_sales: 0,
          quantity_variance: 0,
          revenue_variance: 0,
        }
      );
    }

    const uniqueGameIds = new Set();
    return data.reduce(
      (acc, item) => {
        const isNewGame = !uniqueGameIds.has(item.gameId);
        if (isNewGame) uniqueGameIds.add(item.gameId);

        return {
          qty_sold: acc.qty_sold + item.qtySold,
          sold_amount: acc.sold_amount + item.inventorySoldAmount,
          scratcher_count: acc.scratcher_count + (isNewGame ? item.posQty : 0),
          scratcher_sales: acc.scratcher_sales + (isNewGame ? item.posSales : 0),
          quantity_variance: acc.quantity_variance + (isNewGame ? item.quantityVariance : 0),
          revenue_variance: acc.revenue_variance + (isNewGame ? item.revenueVariance : 0),
        };
      },
      {
        qty_sold: 0,
        sold_amount: 0,
        scratcher_count: 0,
        scratcher_sales: 0,
        quantity_variance: 0,
        revenue_variance: 0,
      }
    );
  }, [data, isMovedToBackstockView]);

  const handleGameClick = useCallback((gameId: string) => {
    navigate(`/store/${id}/scratcher/${gameId}/packlifecycle`);
  }, [navigate, id]);

  const handlePackClick = useCallback((gameId: string, packId: string) => {
    navigate(`/store/${id}/scratcher/${gameId}/packlifecycle/${packId}?sold_out=true`);
  }, [navigate, id]);

  const getUnknownPackTitle = useCallback((item: AuditDetailItem) => {
    if (item.packId !== 'UNKNOWN_PACK') return undefined;
    return item.validationMessage || undefined;
  }, []);

  const handleMarkMovedPackSoldOut = useCallback(async (item: any) => {
    if (!id || !item?.gameId || !item?.packId) {
      return;
    }

    const rowKey = `${item.gameId}-${item.packId}`;
    setMarkingSoldOutKey(rowKey);
    const response = await bulkMarkManualSoldOut(id, [{ game_id: String(item.gameId), pack_id: String(item.packId) }]);
    setMarkingSoldOutKey(null);

    if (!response.success) {
      toast.error(response.error || 'Failed to mark pack sold out');
      return;
    }

    const result = response.data?.results?.[0];
    if (!result || result.status !== 'marked') {
      toast.error(result?.message || response.error || 'Failed to mark pack sold out');
      return;
    }

    toast.success('Pack marked sold out');
    await Promise.all([
      auditDate
        ? queryClient.invalidateQueries({ queryKey: ['daily-expanded-report', id, auditDate] })
        : Promise.resolve(),
      queryClient.invalidateQueries({ queryKey: ['pack-lifecycle', id, String(item.gameId), String(item.packId)] }),
      queryClient.invalidateQueries({ queryKey: ['game-history', id, String(item.gameId)] }),
      queryClient.invalidateQueries({ queryKey: ['game-pack-report', id] }),
      queryClient.invalidateQueries({ queryKey: ['current-inventory-status', id] }),
      queryClient.invalidateQueries({ queryKey: ['lottery-packs', id] }),
      queryClient.invalidateQueries({ queryKey: ['manual-sold-out-eligibility', id] }),
    ]);
  }, [auditDate, id, queryClient]);

  return (
    <TableContainer
      component={Paper}
      className="audit-detail-table"
      sx={{
        ...tableContainerStyle,
        marginTop: 0,
        borderTopLeftRadius: 15,
        borderTopRightRadius: (isBothConnected || isPOSConnectedOnly) ? 0 :0,
        '& th:last-of-type': { borderRight: 'none' }
      }}
    >

      <Table size="small" stickyHeader>

        <TableHead>
          {isMovedToBackstockView ? (
            <TableRow>
              <TableCell
                className={`${getCompleteCellClass({
                  rowIdx: 0,
                  colIdx: 0,
                  totalRows: 1,
                  totalCols: TOTAL_COLS
                })} game-info-cell`}
                sx={{ width: '420px', minWidth: '420px' }}
              >
                <p className="parent-header-text">Game</p>
              </TableCell>
              <TableCell
                className={`${getCompleteCellClass({
                  rowIdx: 0,
                  colIdx: 1,
                  totalRows: 1,
                  totalCols: TOTAL_COLS
                })}`}
                sx={{ width: '160px', minWidth: '160px', textAlign: 'left' }}
              >
                <p className="parent-header-text" style={{ textAlign: 'left', width: '100%' }}>Pack</p>
              </TableCell>
              {['Range', 'Qty', 'Value', 'Current Status'].map((text, idx) => (
                <TableCell
                  key={text}
                  className={getCompleteCellClass({
                    rowIdx: 0,
                    colIdx: 2 + idx,
                    totalRows: 1,
                    totalCols: TOTAL_COLS,
                    borderColumns
                  })}
                  sx={{ width: idx === 3 ? '150px' : '100px', minWidth: idx === 3 ? '150px' : '100px', textAlign: idx === 0 || idx === 3 ? 'left' : 'right' }}
                >
                  <p
                    className="parent-header-text"
                    style={{ textAlign: idx === 0 || idx === 3 ? 'left' : 'right', width: '100%' }}
                  >
                    {text}
                  </p>
                </TableCell>
              ))}
            </TableRow>
          ) : (
            <>
              <TableRow>
                {/* Game */}
                <TableCell
                  rowSpan={2}
                  className={`${getCompleteCellClass({
                    rowIdx: 0,
                    colIdx: 0,
                    totalRows: 1,
                    totalCols: TOTAL_COLS
                  })} game-info-cell`}
                  sx={{ width: '420px', minWidth: '420px' }}
                >
                  <p className="parent-header-text">Game</p>
                </TableCell>

                {/* Pack */}
                <TableCell
                  rowSpan={2}
                  className={`${getCompleteCellClass({
                    rowIdx: 0,
                    colIdx: 1,
                    totalRows: 1,
                    totalCols: TOTAL_COLS
                  })} audit-detail-pack-cell`}
                  sx={{ width: '160px', minWidth: '160px', }}
                >
                  <p className="parent-header-text">Pack</p>
                </TableCell>

                {/* Inventory */}
                <Header
                  title="Inventory"
                  subtitle="Actual Physical Count"
                  colSpan={3}
                  cellStyle={{
                    className: getCompleteCellClass({
                      rowIdx: 0,
                      colIdx: 2,
                      totalRows: 2,
                      totalCols: TOTAL_COLS
                    })
                  }}
                />

                {/* POS - Only show if hasPosType */}
                {hasPosType && (
                  <Header
                    title="POS"
                    subtitle="Reported Sales"
                    colSpan={2}
                    cellStyle={{
                      className: getCompleteCellClass({
                        rowIdx: 0,
                        colIdx: 5,
                        totalRows: 2,
                        totalCols: TOTAL_COLS
                      }),
                      sx: { width: '200px', minWidth: '200px' }
                    }}
                  />
                )}

                {/* Reconciliation - Only show if hasPosType */}
                {hasPosType && (
                  <Header
                    title="Reconciliation"
                    subtitle="POS vs Inventory"
                    colSpan={2}
                    cellStyle={{
                      className: getCompleteCellClass({
                        rowIdx: 0,
                        colIdx: 7,
                        totalRows: 2,
                        totalCols: TOTAL_COLS

                      }),
                      sx: { width: '240px', minWidth: '240px' }
                    }}
                  />
                )}

              </TableRow>

              {/* ===== Sub Headers ===== */}
              <TableRow>
                {['Range', 'Sold Qty', 'Sold $'].map((text, idx) => (
                  <TableCell
                    key={text}
                    className={`${getCompleteCellClass({
                      rowIdx: 'subheader',
                      colIdx: 2 + idx,
                      totalRows: 2,
                      totalCols: TOTAL_COLS,
                      borderColumns
                    })}${idx === 0 ? ' audit-detail-range-cell' : ''}`}
                    sx={{
                      width: idx === 0 ? '120px' : '100px',
                      minWidth: idx === 0 ? '120px' : '100px'
                    }}
                  >
                    {text}
                  </TableCell>
                ))}

                {/* POS Sub-headers - Only show if hasPosType */}
                {hasPosType && ['Sold Qty', 'Sales $'].map((text, idx) => (
                  <TableCell
                    key={text}
                    className={getCompleteCellClass({
                      rowIdx: 'subheader',
                      colIdx: 5 + idx,
                      totalRows: 2,
                      totalCols: TOTAL_COLS,
                      borderColumns
                    })}
                    sx={{ width: '100px', minWidth: '100px' }}
                  >
                    {text}
                  </TableCell>
                ))}

                {/* Reconciliation Sub-headers - Only show if hasPosType */}
                {hasPosType && ['Variance Qty', 'Variance'].map((text, idx) => (
                  <TableCell
                    key={text}
                    className={getCompleteCellClass({
                      rowIdx: 'subheader',
                      colIdx: 7 + idx,
                      totalRows: 2,
                      totalCols: TOTAL_COLS,
                      borderColumns
                    })}
                    sx={{ width: '100px', minWidth: '100px' }}
                  >
                    {text}
                  </TableCell>
                ))}
              </TableRow>
            </>
          )}
        </TableHead>

        {/* ================= BODY ================= */}
        <TableBody>
          {isLoading ? (
            <TableRow>
              <TableCell colSpan={TOTAL_COLS} sx={{ textAlign: 'center', padding: '40px 20px' }}>
                <Box display="flex" justifyContent="center" alignItems="center" flexDirection="column" gap={2}>
                  <CircularProgress size={40} />
                  <p>Loading audit data...</p>
                </Box>
              </TableCell>
            </TableRow>
          ) : data.length === 0 ? (
            <TableRow>
              <TableCell colSpan={TOTAL_COLS}>
                <p className="text-align-center ">No Audit Data Found</p>
              </TableCell>
            </TableRow>
          ) : isMovedToBackstockView ? (
            data.map((item, rowIdx) => {
              const gName = item.gameName;
              const gId = item.gameId;
              const gCost = item.costPerTicket;
              const gPlu = item.plu;
              const pId = item.packId;
              const startT = item.startingTicket;
              const endT = item.endingTicket;
              const startDisplay = item.startingTicketDisplay;
              const endDisplay = item.endingTicketDisplay;
              const qSold = item.qtySold;
              const invSoldAmt = item.inventorySoldAmount;
              const hasNoPack = !pId || pId === '';
              const unknownPackTitle = getUnknownPackTitle(item);
              const isUnknownPack = pId === 'UNKNOWN_PACK';
              const rowSoldOutKey = `${gId}-${pId}`;
              const eligibility = manualSoldOutEligibilityByKey.get(getEligibilityKey(gId, pId));
              const isEligibilityLoading = isManualSoldOutEligibilityLoading && !eligibility;
              const canMarkRowSoldOut = Boolean(id && gId && pId && eligibility?.can_mark_manual_sold_out);
              const isMarkingRowSoldOut = markingSoldOutKey === rowSoldOutKey;
              const returnedToCounterAt = eligibility?.returned_to_counter_at ?? null;
              const backOnSaleDestination = getBackOnSaleDestination(eligibility?.current_subgroup, Boolean(returnedToCounterAt));
              const backstockLocationLabel = getBackstockLocationLabel(eligibility?.current_subgroup);
              const backOnSaleTimestamp = formatBackOnSaleTimestamp(returnedToCounterAt);
              const ineligibleLabel = returnedToCounterAt
                ? `Back on Sale${backOnSaleDestination ? `: ${backOnSaleDestination}` : ''} ${backOnSaleTimestamp}`
                : (eligibility?.message || 'Not eligible');

              return (
                <TableRow
                  key={item.id || rowIdx}
                  className={rowIdx % 2 === 0 ? "audit-table__row--even" : "audit-table__row--odd"}
                  sx={{ backgroundColor: getRowBgColor(item) }}
                >
                  {/* Game */}
                  <TableCell className={`${getDataCellClass(0, rowIdx, data.length, borderColumns, TOTAL_COLS)} game-info-cell`}>
                    <div className="game-info-wrapper">
                      <div className="game-details-left">
                        <p
                          className="game-name-new"
                          onClick={() => handleGameClick(gId)}
                        >
                          {gName}
                        </p>
                        <p className="game-plu-new">{gPlu}</p>
                      </div>
                      <p className="game-cost-new">
                        {formatCurrency(gCost)}
                      </p>
                    </div>
                  </TableCell>

                  {/* Pack */}
                  <TableCell
                    className={`${getDataCellClass(1, rowIdx, data.length, borderColumns, TOTAL_COLS)}${!isMovedToBackstockView ? ' audit-detail-pack-cell' : ''}`}
                    sx={isMovedToBackstockView ? { textAlign: 'left' } : undefined}
                  >
                    {hasNoPack ? (
                      <div className="pack-id-container">
                        <span
                          className="pack-link"
                          onClick={() => handleGameClick(gId)}
                        >
                          {gId}
                        </span>
                      </div>
                    ) : (
                      <>
                        <div className="pack-id-container">
                          <span
                            className="pack-link"
                            onClick={() => handleGameClick(gId)}
                          >
                            {gId}
                          </span>
                          <span>-</span>
                          <span
                            className="pack-link"
                            onClick={isUnknownPack ? undefined : () => handlePackClick(gId, pId)}
                            title={unknownPackTitle}
                            style={isUnknownPack ? { cursor: 'default' } : undefined}
                          >
                            {pId}
                          </span>
                        </div>
                        {item.isManualSoldOut ? (
                          <span className="audit-detail-manual-sold-out-tag">Manual Sold Out</span>
                        ) : null}
                      </>
                    )}
                  </TableCell>

                  {/* Inventory - Range */}
                  <TableCell
                    className={`${getDataCellClass(2, rowIdx, data.length, borderColumns, TOTAL_COLS)}${!isMovedToBackstockView ? ' audit-detail-range-cell' : ''}`}
                    sx={isMovedToBackstockView ? { '&&': { textAlign: 'left !important' } } : undefined}
                  >
                    {isMovedToBackstockView
                      ? getMovedRange(item)
                      : (hasNoPack ? '-' : (
                        <>
                          {getTicketDisplay(startDisplay, startT)}
                          -
                          {getTicketDisplay(endDisplay, endT)}
                        </>
                      ))
                    }
                  </TableCell>

                  <TableCell
                    className={getDataCellClass(3, rowIdx, data.length, borderColumns, TOTAL_COLS)}
                    sx={isMovedToBackstockView ? { textAlign: 'right' } : undefined}
                  >
                    {isMovedToBackstockView ? getMovedQty(item) : (hasNoPack ? '-' : qSold)}
                  </TableCell>

                  <TableCell
                    className={getDataCellClass(4, rowIdx, data.length, borderColumns, TOTAL_COLS)}
                    sx={isMovedToBackstockView ? { textAlign: 'right' } : undefined}
                  >
                    {isMovedToBackstockView ? formatCurrency(getMovedValue(item)) : (hasNoPack ? '-' : formatCurrency(invSoldAmt))}
                  </TableCell>

                  <TableCell
                    className={getDataCellClass(5, rowIdx, data.length, borderColumns, TOTAL_COLS)}
                    sx={{ textAlign: 'left' }}
                  >
                    {isEligibilityLoading ? (
                      <CircularProgress size={18} />
                    ) : canMarkRowSoldOut ? (
                      <Tooltip title="Mark this pack sold out as a correction">
                        <span className="audit-detail-backstock-state-note">
                          <span className="audit-detail-backstock-state-card">
                            <span className="audit-detail-backstock-state-label">Backstock</span>
                            <span className="audit-detail-backstock-state-destination">{backstockLocationLabel}</span>
                            <Button
                              size="small"
                              variant="text"
                              className="audit-detail-mark-sold-out-link"
                              disabled={Boolean(markingSoldOutKey)}
                              onClick={() => handleMarkMovedPackSoldOut(item)}
                            >
                              {isMarkingRowSoldOut ? <CircularProgress size={14} color="inherit" /> : 'Mark Sold Out'}
                            </Button>
                          </span>
                        </span>
                      </Tooltip>
                    ) : (
                      <Tooltip title={eligibility?.message || 'Pack is not currently eligible to mark sold out'}>
                        <span className="audit-detail-backstock-state-note">
                          {returnedToCounterAt ? (
                            <span className="audit-detail-backstock-state-card">
                              <span className="audit-detail-backstock-state-label">Back on Sale</span>
                              {backOnSaleDestination ? (
                                <span className="audit-detail-backstock-state-destination">{backOnSaleDestination}</span>
                              ) : null}
                              <span className="audit-detail-backstock-state-timestamp">{backOnSaleTimestamp}</span>
                            </span>
                          ) : (
                            ineligibleLabel
                          )}
                        </span>
                      </Tooltip>
                    )}
                  </TableCell>

                </TableRow>
              );
            })
          ) : (
            groupedDefaultRows.map(({ item, isFirstInGroup, groupSize, globalRowIdx, rowColor, groupStripeClass }) => {
              const gName = item.gameName;
              const gId = item.gameId;
              const gCost = item.costPerTicket;
              const gPlu = item.plu;
              const pId = item.packId;
              const startT = item.startingTicket;
              const endT = item.endingTicket;
              const startDisplay = item.startingTicketDisplay;
              const endDisplay = item.endingTicketDisplay;
              const qSold = item.qtySold;
              const invSoldAmt = item.inventorySoldAmount;
              const pQty = item.posQty;
              const pSales = item.posSales;
              const vQty = item.quantityVariance;
              const vAmt = item.revenueVariance;
              const hasNoPack = !pId || pId === '';
              const unknownPackTitle = getUnknownPackTitle(item);
              const isUnknownPack = pId === 'UNKNOWN_PACK';

              return (
                <TableRow
                  key={item.id || globalRowIdx}
                  className={groupStripeClass}
                  sx={{ backgroundColor: rowColor }}
                >
                  {isFirstInGroup && (
                    <TableCell
                      rowSpan={groupSize}
                      className={`${getDataCellClass(0, globalRowIdx, groupedDefaultRows.length, borderColumns, TOTAL_COLS)} game-info-cell`}
                    >
                      <div className="game-info-wrapper">
                        <div className="game-details-left">
                          <p
                            className="game-name-new"
                            onClick={() => handleGameClick(gId)}
                          >
                            {gName}
                          </p>
                          <p className="game-plu-new">{gPlu}</p>
                        </div>
                        <p className="game-cost-new">
                          {formatCurrency(gCost)}
                        </p>
                      </div>
                    </TableCell>
                  )}

                  <TableCell
                    className={`${getDataCellClass(1, globalRowIdx, groupedDefaultRows.length, borderColumns, TOTAL_COLS)} audit-detail-pack-cell`}
                  >
                    {hasNoPack ? (
                      <div className="pack-id-container">
                        <span
                          className="pack-link"
                          onClick={() => handleGameClick(gId)}
                        >
                          {gId}
                        </span>
                      </div>
                    ) : (
                      <>
                        <div className="pack-id-container">
                          <span
                            className="pack-link"
                            onClick={() => handleGameClick(gId)}
                          >
                            {gId}
                          </span>
                          <span>-</span>
                          <span
                            className="pack-link"
                            onClick={isUnknownPack ? undefined : () => handlePackClick(gId, pId)}
                            title={unknownPackTitle}
                            style={isUnknownPack ? { cursor: 'default' } : undefined}
                          >
                            {pId}
                          </span>
                        </div>
                        {item.isManualSoldOut ? (
                          <span className="audit-detail-manual-sold-out-tag">Manual Sold Out</span>
                        ) : null}
                      </>
                    )}
                  </TableCell>

                  <TableCell
                    className={`${getDataCellClass(2, globalRowIdx, groupedDefaultRows.length, borderColumns, TOTAL_COLS)} audit-detail-range-cell`}
                  >
                    {hasNoPack ? (
                      '-'
                    ) : (
                      <>
                        {getTicketDisplay(startDisplay, startT)}
                        -
                        {getTicketDisplay(endDisplay, endT)}
                      </>
                    )}
                  </TableCell>

                  <TableCell className={getDataCellClass(3, globalRowIdx, groupedDefaultRows.length, borderColumns, TOTAL_COLS)}>
                    {hasNoPack ? '-' : qSold}
                  </TableCell>

                  <TableCell className={getDataCellClass(4, globalRowIdx, groupedDefaultRows.length, borderColumns, TOTAL_COLS)}>
                    {hasNoPack ? '-' : formatCurrency(invSoldAmt)}
                  </TableCell>

                  {hasPosType && isFirstInGroup && (
                    <>
                      <TableCell
                        rowSpan={groupSize}
                        className={getDataCellClass(5, globalRowIdx, groupedDefaultRows.length, borderColumns, TOTAL_COLS)}
                      >
                        {pQty}
                      </TableCell>

                      <TableCell
                        rowSpan={groupSize}
                        className={getDataCellClass(6, globalRowIdx, groupedDefaultRows.length, borderColumns, TOTAL_COLS)}
                      >
                        {formatCurrency(pSales)}
                      </TableCell>

                      <TableCell
                        rowSpan={groupSize}
                        className={getDataCellClass(7, globalRowIdx, groupedDefaultRows.length, borderColumns, TOTAL_COLS)}
                      >
                        {vQty === 0 ? (
                          <img
                            src={noVarianceIcon}
                            alt="No variance"
                            width={20}
                            height={20}
                          />
                        ) : (
                          vQty
                        )}
                      </TableCell>

                      <TableCell
                        rowSpan={groupSize}
                        className={getDataCellClass(8, globalRowIdx, groupedDefaultRows.length, borderColumns, TOTAL_COLS)}
                      >
                        {vAmt === 0 ? (
                          <img
                            src={noVarianceIcon}
                            alt="No variance"
                            width={20}
                            height={20}
                          />
                        ) : (
                          formatCurrency(vAmt)
                        )}
                      </TableCell>
                    </>
                  )}
                </TableRow>
              );
            })
          )}

          {/* ================= TOTAL ROW ================= */}
          {data.length > 0 && (
            <TableRow sx={{ backgroundColor: 'rgba(243, 242, 247, 1)' }}>
              {Array.from({ length: TOTAL_COLS }).map((_, colIdx) => {
                const getTotalCellContent = () => {
                  if (colIdx === 0) return 'Total';
                  if (colIdx === 1) return `${totalPacksCount} ${totalPacksCount === 1 ? 'pack' : 'packs'}`;
                  if (colIdx === 3) return totals.qty_sold;
                  if (colIdx === 4) return formatCurrency(totals.sold_amount);

                  if (!isMovedToBackstockView && hasPosType) {
                    if (colIdx === 5) return totals.scratcher_count;
                    if (colIdx === 6) return formatCurrency(totals.scratcher_sales);
                    if (colIdx === 7) return totals.quantity_variance;
                    if (colIdx === 8) return formatCurrency(totals.revenue_variance);
                  }

                  return '';
                };

                const getVarianceColor = () => {
                  if (isMovedToBackstockView || !hasPosType) return undefined;

                  if (colIdx === 7) {
                    if (totals.quantity_variance > 0) return '#0000FF';
                    if (totals.quantity_variance < 0) return '#FF0000';
                  }

                  if (colIdx === 8) {
                    if (totals.revenue_variance > 0) return '#0000FF';
                    if (totals.revenue_variance < 0) return '#FF0000';
                  }

                  return undefined;
                };

                const content = getTotalCellContent();
                const varianceColor = getVarianceColor();
                const totalRowAlign = isMovedToBackstockView
                  ? (colIdx === 0 ? 'start' : colIdx === 1 || colIdx === 2 ? 'left' : 'end')
                  : (colIdx === 0 ? 'start' : colIdx === 1 || colIdx === 2 ? 'center' : 'end');

                const sxStyles: any = {
                  '&&': {
                    fontWeight: '600!important',
                    textAlign: `${totalRowAlign} !important`,
                    paddingLeft: `${colIdx === 1 ? '12px' : colIdx !== 0 ? '4px' : '22px'} !important`,
                    paddingRight: `${colIdx === 1 ? '0' : colIdx !== 0 && colIdx !== 1 ? '12px' : '4px'} !important`,
                    fontSize: `18px !important`,
                  }
                };

                if (varianceColor) {
                  sxStyles['&&'].color = `${varianceColor} !important`;
                }

                return (
                  <TableCell
                    key={colIdx}
                    className={`${getCompleteCellClass({
                      type: 'cells',
                      rowIdx: 'total',
                      colIdx,
                      totalRows: 10,
                      totalCols: TOTAL_COLS,
                      borderColumns
                    })} parent-header-text${colIdx === 1 ? ' audit-detail-pack-cell' : ''}${colIdx === 2 ? ' audit-detail-range-cell' : ''}`}
                    sx={sxStyles}
                  >
                    {content}
                  </TableCell>
                );
              })}
            </TableRow>
          )}
          {!isLoading && visibleAuditNotes.length > 0 && (
            <TableRow>
              <TableCell
                colSpan={TOTAL_COLS}
                sx={{
                  '&&': {
                    borderTop: '1px solid rgba(163, 174, 208, 0.35)',
                    backgroundColor: '#FFFBEB',
                    color: '#5f667b',
                    fontSize: '12px !important',
                    lineHeight: '1.35 !important',
                    padding: '10px 14px !important',
                    textAlign: 'left !important',
                    whiteSpace: 'normal',
                  },
                }}
              >
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', textAlign: 'left', width: '100%' }}>
                  <Box sx={{ fontSize: 14, lineHeight: 1.35, fontWeight: 800, color: '#92400E' }}>
                    Cashier Notes
                  </Box>
                  {visibleAuditNotes.map((entry, index) => {
                    const timestamp = formatAuditNoteTimestamp(entry);
                    const author = entry.author_name?.trim();
                    return (
                      <Box
                        key={entry.id || `${entry.created_at || 'note'}-${index}`}
                        sx={{
                          width: '100%',
                          borderTop: '1px solid #FDE68A',
                          pt: 1,
                          mt: 1,
                        }}
                      >
                        <Box sx={{ color: '#222b45', fontSize: 14, lineHeight: 1.45, whiteSpace: 'pre-wrap', textAlign: 'left' }}>
                          {entry.text}
                        </Box>
                        {(author || timestamp) ? (
                          <Box sx={{ mt: 0.35, color: '#7b8194', fontSize: 12, lineHeight: 1.35, textAlign: 'left' }}>
                            {[author, timestamp].filter(Boolean).join(' - ')}
                          </Box>
                        ) : null}
                      </Box>
                    );
                  })}
                </Box>
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
    </TableContainer>
  );
}
